// src/pages/analytics/PurchasingPipelineTab.tsx
//
// v3.6.0 — Purchasing Pipeline page. Container/CBM editor that back-writes
// to Advantage shpschd. Phase C of the Blue Sky inventory vision. The
// existing /analytics/inventory tab is the triage view (what to reorder);
// this tab is the execution view (where the order goes — which container,
// which ETA, what CBM).
//
// Phase 1 (v3.6.0): list view + create modal.
// Phase 2 (this file): click a row -> detail/edit drawer. Edit header +
// status, add/remove lines (each back-writes shpschd), per-line write
// status with retry surfacing, and delete container.

import { useState, useRef, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, RefreshCw, Truck, AlertTriangle, X, Trash2, Pencil, Save,
  Check, CircleAlert, Loader2,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { hebrewDateCaption } from '../../components/DateField';
import HistoryPanel from '../../components/HistoryPanel';
import type {
  ContainerSummary, ContainerDetail, ContainerLine,
  ContainerCreateRequest, ContainerCreateLine, ContainerPatchRequest, ContainerStatus,
} from '../../types';

const STATUS_LABELS: Record<ContainerStatus, { label: string; cls: string }> = {
  planned:    { label: 'Planned',    cls: 'bg-slate-700/50 text-slate-300 border-slate-600/50' },
  scheduled:  { label: 'Scheduled',  cls: 'bg-blue-500/15 text-blue-200 border-blue-500/30' },
  in_transit: { label: 'In Transit', cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' },
  received:   { label: 'Received',   cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
  confirmed:  { label: 'Confirmed',  cls: 'bg-violet-500/15 text-violet-200 border-violet-500/30' },
  closed:     { label: 'Closed',     cls: 'bg-slate-800/50 text-slate-500 border-slate-700/50' },
};
const STATUS_ORDER: ContainerStatus[] = ['planned', 'scheduled', 'in_transit', 'received', 'confirmed', 'closed'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
function fmtNum(n: number | null, digits = 0): string {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}
// ISO -> YYYY-MM-DD for <input type="date">
function dateInput(iso: string | null): string {
  if (!iso) return '';
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

export function PurchasingPipelineTab() {
  const [statusFilter, setStatusFilter] = useState<'' | ContainerStatus>('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['containers', 'list', statusFilter],
    queryFn: () => api.listContainers(statusFilter || undefined),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Truck size={20} /> Purchasing Pipeline
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Plan shipping containers, assign POs, track ETAs. Each line back-writes to Advantage shpschd.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ContainerStatus | '')}
            className="bg-slate-800/60 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-1.5"
          >
            <option value="">All statuses</option>
            <option value="planned">Planned</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_transit">In Transit</option>
            <option value="received">Received</option>
            <option value="closed">Closed</option>
          </select>
          <button
            onClick={() => listQ.refetch()}
            className="p-2 rounded-md bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700/60"
            title="Refresh"
          >
            <RefreshCw size={14} className={listQ.isFetching ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/40 text-blue-100 text-sm font-medium hover:bg-blue-500/30 flex items-center gap-1.5"
          >
            <Plus size={14} /> New shipment
          </button>
        </div>
      </div>

      {listQ.isLoading && (
        <div className="p-8 text-center text-slate-400 text-sm">Loading containers…</div>
      )}

      {listQ.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load containers</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(listQ.error as Error).message}</div>
            <div className="text-[11px] text-red-300/50 mt-1">
              The agent may not yet be on v3.6.0 — confirm <code>/api/health</code> reports 3.6.0+.
            </div>
          </div>
        </div>
      )}

      {!listQ.isLoading && !listQ.error && (
        <ContainerTable rows={listQ.data ?? []} onSelect={(id) => setSelectedId(id)} />
      )}

      {showCreate && (
        <CreateContainerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); listQ.refetch(); }}
        />
      )}

      {selectedId && (
        <ContainerDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.refetch()}
          onDeleted={() => { setSelectedId(null); listQ.refetch(); }}
        />
      )}
    </div>
  );
}

// ── List table ──────────────────────────────────────────────────────

function ContainerTable({ rows, onSelect }: { rows: ContainerSummary[]; onSelect: (id: string) => void }) {
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500 text-sm border border-slate-800 rounded-lg">
        No containers yet. Click <strong>New container</strong> to plan your first shipment.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-slate-800 rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
          <tr>
            {/* Sticky identifying column — opaque bg so scrolled content doesn't show through */}
            <th className="sticky left-0 z-10 bg-slate-900 px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Vendor</th>
            <th className="px-3 py-2 text-left">Ship</th>
            <th className="px-3 py-2 text-left">ETA</th>
            <th className="px-3 py-2 text-right">Lines</th>
            <th className="px-3 py-2 text-right">CBM</th>
            <th className="px-3 py-2 text-right">Cases</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((c) => {
            const sty = STATUS_LABELS[c.status] ?? STATUS_LABELS.planned;
            return (
              <tr
                key={c.id}
                onClick={() => onSelect(c.id)}
                className="group hover:bg-slate-800/30 transition-colors cursor-pointer"
              >
                <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-slate-100 font-medium">{c.name}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${sty.cls}`}>
                    {sty.label}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-300">{c.vendor ?? '—'}</td>
                <td className="px-3 py-2 text-slate-300">{fmtDate(c.shipDate)}</td>
                <td className="px-3 py-2 text-slate-300">{fmtDate(c.eta)}</td>
                <td className="px-3 py-2 text-right text-slate-300">{c.lineCount}</td>
                <td className="px-3 py-2 text-right text-slate-300">{fmtNum(c.totalCbm ?? c.totalLineCbm, 1)}</td>
                <td className="px-3 py-2 text-right text-slate-300">{fmtNum(c.totalCases, 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail / edit drawer (Phase 2) ──────────────────────────────────

function ContainerDrawer({
  id, onClose, onChanged, onDeleted,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const detailKey = ['containers', 'detail', id];
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => api.getContainer(id),
    refetchOnWindowFocus: false,
  });

  const [editing, setEditing] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  // Track mousedown origin so a drag-select that ends outside the panel
  // doesn't close the drawer (see feedback_modal_drag_select_bug).
  const backdropMouseDown = useRef(false);

  // Re-sync detail into all consumers + the parent list after any mutation.
  function refreshAll(detail?: ContainerDetail) {
    if (detail) qc.setQueryData(detailKey, detail);
    qc.invalidateQueries({ queryKey: detailKey });
    // History lives under a sibling key (not a prefix of detailKey), so
    // invalidate it explicitly — a create/update/line change is a new event.
    qc.invalidateQueries({ queryKey: ['containers', 'history', id] });
    onChanged();
  }

  const detail = detailQ.data;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end"
      onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropMouseDown.current) onClose();
        backdropMouseDown.current = false;
      }}
    >
      <div className="bg-slate-900 border-l border-slate-700 w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Truck size={18} className="text-blue-300 shrink-0" />
            <h3 className="text-lg font-semibold text-white truncate">
              {detail?.name ?? 'Container'}
            </h3>
            {detail && (
              <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${STATUS_LABELS[detail.status].cls}`}>
                {STATUS_LABELS[detail.status].label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {detailQ.isLoading && (
          <div className="p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading container…
          </div>
        )}
        {detailQ.error && (
          <div className="m-5 p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm">
            <div className="font-medium flex items-center gap-2"><AlertTriangle size={14} /> Couldn't load container</div>
            <div className="text-xs text-red-300/70 mt-1">{(detailQ.error as Error).message}</div>
          </div>
        )}

        {detail && (
          <div className="p-5 space-y-6">
            {banner && (
              <div className="text-sm text-amber-200 bg-amber-950/30 border border-amber-900/40 rounded px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{banner}</span>
              </div>
            )}

            {editing ? (
              <HeaderEditor
                detail={detail}
                onCancel={() => setEditing(false)}
                onSaved={(d) => { setEditing(false); refreshAll(d); }}
              />
            ) : (
              <HeaderView
                detail={detail}
                onEdit={() => setEditing(true)}
                onStatusChange={(d) => refreshAll(d)}
                onBanner={setBanner}
              />
            )}

            <LinesSection
              detail={detail}
              onChanged={(d, msg) => { refreshAll(d); if (msg) setBanner(msg); }}
            />

            {/* Change history — who created / updated the container + lines,
                and when. Lazy-loaded; visible to any user on the page. */}
            <HistoryPanel
              title="Change History"
              queryKey={['containers', 'history', id]}
              fetcher={() => api.getContainerHistory(id)}
            />

            <DangerZone
              id={id}
              onDeleted={onDeleted}
              onBanner={setBanner}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Header (read-only view + status quick-change) ───────────────────

function HeaderView({
  detail, onEdit, onStatusChange, onBanner,
}: {
  detail: ContainerDetail;
  onEdit: () => void;
  onStatusChange: (d: ContainerDetail) => void;
  onBanner: (msg: string | null) => void;
}) {
  const statusM = useMutation({
    mutationFn: (status: ContainerStatus) => api.patchContainer(detail.id, { status }),
    onSuccess: (d) => onStatusChange(d),
    onError: (e: Error) => onBanner(`Status update failed: ${e.message}`),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-300">Details</h4>
        <button
          onClick={onEdit}
          className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-1"
        >
          <Pencil size={12} /> Edit
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Detail label="Vendor" value={detail.vendor ?? '—'} />
        <div>
          <dt className="text-xs text-slate-500 mb-1">Status</dt>
          <select
            value={detail.status}
            disabled={statusM.isPending}
            onChange={(e) => statusM.mutate(e.target.value as ContainerStatus)}
            className="bg-slate-800/60 border border-slate-700 text-slate-200 text-sm rounded px-2 py-1 disabled:opacity-50"
          >
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s].label}</option>
            ))}
          </select>
        </div>
        <Detail label="Ship date" value={fmtDate(detail.shipDate)} />
        <Detail label="ETA" value={fmtDate(detail.eta)} />
        <Detail label="Cartons" value={fmtNum(detail.cartons, 0)} />
        <Detail label="Total CBM" value={fmtNum(detail.totalCbm ?? detail.totalLineCbm, 1)} />
        <Detail label="Total cases" value={fmtNum(detail.totalCases, 0)} />
        <Detail
          label="Destination"
          value={detail.dropshipCustomerCode != null
            ? `Drop-ship → ${detail.dropshipCustomerName || detail.dropshipCustomerCode}${detail.dropshipOrderNo ? ` (order ${detail.dropshipOrderNo})` : ''}`
            : 'Warehouse'}
        />
        <Detail
          label="AP invoice"
          value={detail.apInvoiceNo ? `#${detail.apInvoiceNo}` : 'Not yet created'}
        />
      </dl>
      {detail.apInvoiceError && (
        <div className="text-xs text-red-300 bg-red-950/30 border border-red-900/40 rounded px-2 py-1.5 flex items-center gap-1.5">
          <AlertTriangle size={12} /> AP invoice write failed: {detail.apInvoiceError}
        </div>
      )}
      {detail.notes && (
        <div>
          <div className="text-xs text-slate-500 mb-1">Notes</div>
          <div className="text-sm text-slate-300 whitespace-pre-wrap">{detail.notes}</div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 mb-1">{label}</dt>
      <dd className="text-slate-200">{value}</dd>
    </div>
  );
}

// ── Header editor ────────────────────────────────────────────────────

function HeaderEditor({
  detail, onCancel, onSaved,
}: {
  detail: ContainerDetail;
  onCancel: () => void;
  onSaved: (d: ContainerDetail) => void;
}) {
  const [name, setName] = useState(detail.name);
  const [vendor, setVendor] = useState(detail.vendor ?? '');
  const [shipDate, setShipDate] = useState(dateInput(detail.shipDate));
  const [eta, setEta] = useState(dateInput(detail.eta));
  const [totalCbm, setTotalCbm] = useState(detail.totalCbm == null ? '' : String(detail.totalCbm));
  const [totalCases, setTotalCases] = useState(detail.totalCases == null ? '' : String(detail.totalCases));
  const [notes, setNotes] = useState(detail.notes ?? '');
  const [err, setErr] = useState<string | null>(null);

  const saveM = useMutation({
    mutationFn: (body: ContainerPatchRequest) => api.patchContainer(detail.id, body),
    onSuccess: (d) => onSaved(d),
    onError: (e: Error) => setErr(e.message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required'); return; }
    // Nullable string fields use *Set flags so clearing them is honoured.
    saveM.mutate({
      name: name.trim(),
      vendor: vendor.trim() || null, vendorSet: true,
      shipDate: shipDate || null, shipDateSet: true,
      eta: eta || null,
      totalCbm: totalCbm ? Number(totalCbm) : null,
      totalCases: totalCases ? Number(totalCases) : null,
      notes: notes.trim() || null, notesSet: true,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-300">Edit details</h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name *" value={name} onChange={setName} />
        <Field label="Vendor (Advantage code)" value={vendor} onChange={setVendor} placeholder="Optional" />
        <Field label="Ship date" type="date" value={shipDate} onChange={setShipDate} />
        <Field label="ETA" type="date" value={eta} onChange={setEta} />
        <Field label="Total CBM" type="number" value={totalCbm} onChange={setTotalCbm} placeholder="Optional override" />
        <Field label="Total cases" type="number" value={totalCases} onChange={setTotalCases} placeholder="Optional override" />
      </div>
      <Field label="Notes" value={notes} onChange={setNotes} multiline />

      {err && (
        <div className="text-sm text-red-300 bg-red-950/30 border border-red-900/40 rounded px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saveM.isPending}
          className="px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/40 text-blue-100 hover:bg-blue-500/30 text-sm disabled:opacity-50 flex items-center gap-1.5"
        >
          {saveM.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saveM.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ── Lines section (list + per-line shpschd status + add/remove) ─────

function LinesSection({
  detail, onChanged,
}: {
  detail: ContainerDetail;
  onChanged: (d: ContainerDetail, msg?: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-300">
          Lines <span className="text-slate-500 font-normal">({detail.lines.length})</span>
        </h4>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 flex items-center gap-1"
          >
            <Plus size={12} /> Add line
          </button>
        )}
      </div>

      <div className="border border-slate-800 rounded overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/60 text-slate-400 text-xs">
            <tr>
              <th className="px-2 py-1.5 text-left">PO #</th>
              <th className="px-2 py-1.5 text-left">Item #</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              <th className="px-2 py-1.5 text-right">CBM</th>
              <th className="px-2 py-1.5 text-right">Cases</th>
              <th className="px-2 py-1.5 text-center">shpschd</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {detail.lines.length === 0 && !adding && (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-500 text-xs">
                No lines yet. Add one to write it to Advantage shpschd.
              </td></tr>
            )}
            {detail.lines.map((l) => (
              <LineRow key={`${l.poNumber}::${l.itemNo}`} containerId={detail.id} line={l} onChanged={onChanged} />
            ))}
            {adding && (
              <AddLineRow
                containerId={detail.id}
                onDone={(d, msg) => { setAdding(false); if (d) onChanged(d, msg); }}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ShpschdStatus({ line }: { line: ContainerLine }) {
  if (line.shpschdLastError) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-300" title={line.shpschdLastError}>
        <CircleAlert size={12} /> retry
      </span>
    );
  }
  if (line.shpschdWrittenAt) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300" title={`Written ${fmtDate(line.shpschdWrittenAt)}`}>
        <Check size={12} /> written
      </span>
    );
  }
  return <span className="text-[11px] text-slate-500">—</span>;
}

function LineRow({
  containerId, line, onChanged,
}: {
  containerId: string;
  line: ContainerLine;
  onChanged: (d: ContainerDetail, msg?: string | null) => void;
}) {
  const delM = useMutation({
    mutationFn: () => api.deleteContainerLine(containerId, line.poNumber, line.itemNo),
    onSuccess: (d) => onChanged(d),
    // On failure keep the current detail (pass undefined — refreshAll guards it)
    // and surface the message in the drawer banner.
    onError: (e: Error) =>
      onChanged(undefined as unknown as ContainerDetail, `Remove line failed: ${e.message}`),
  });

  return (
    <tr className="border-t border-slate-800">
      <td className="px-2 py-1.5 text-slate-200">{line.poNumber}</td>
      <td className="px-2 py-1.5 text-slate-200">{line.itemNo}</td>
      <td className="px-2 py-1.5 text-right text-slate-300">{fmtNum(line.quantity, 0)}</td>
      <td className="px-2 py-1.5 text-right text-slate-300">{fmtNum(line.cbm, 2)}</td>
      <td className="px-2 py-1.5 text-right text-slate-300">{fmtNum(line.cases, 0)}</td>
      <td className="px-2 py-1.5 text-center"><ShpschdStatus line={line} /></td>
      <td className="px-2 py-1.5 w-8">
        <button
          onClick={() => delM.mutate()}
          disabled={delM.isPending}
          className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-950/30 disabled:opacity-50"
          title="Remove line"
        >
          {delM.isPending ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
        </button>
      </td>
    </tr>
  );
}

function AddLineRow({
  containerId, onDone,
}: {
  containerId: string;
  onDone: (d: ContainerDetail | null, msg?: string | null) => void;
}) {
  const [po, setPo] = useState('');
  const [item, setItem] = useState('');
  const [qty, setQty] = useState('');
  const [cbm, setCbm] = useState('');
  const [cases, setCases] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const addM = useMutation({
    mutationFn: (line: ContainerCreateLine) => api.addContainerLine(containerId, line),
    onSuccess: (d) => onDone(d),
    onError: (e: Error) => setErr(e.message),
  });

  function submit() {
    setErr(null);
    if (!po.trim() || !item.trim() || !(Number(qty) > 0)) {
      setErr('PO, item, and a positive quantity are required');
      return;
    }
    addM.mutate({
      poNumber: po.trim(),
      itemNo: item.trim(),
      quantity: Number(qty),
      cbm: cbm ? Number(cbm) : null,
      cases: cases ? Number(cases) : null,
    });
  }

  return (
    <>
      <tr className="border-t border-slate-800 bg-slate-800/30">
        <td className="px-1 py-1"><CellInput value={po} onChange={setPo} /></td>
        <td className="px-1 py-1"><CellInput value={item} onChange={setItem} /></td>
        <td className="px-1 py-1"><CellInput type="number" value={qty} onChange={setQty} align="right" /></td>
        <td className="px-1 py-1"><CellInput type="number" value={cbm} onChange={setCbm} align="right" /></td>
        <td className="px-1 py-1"><CellInput type="number" value={cases} onChange={setCases} align="right" /></td>
        <td className="px-2 py-1 text-center text-[11px] text-slate-500">new</td>
        <td className="px-1 py-1 whitespace-nowrap">
          <button
            onClick={submit}
            disabled={addM.isPending}
            className="p-1 rounded text-emerald-400 hover:bg-emerald-950/30 disabled:opacity-50"
            title="Save line"
          >
            {addM.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          </button>
          <button
            onClick={() => onDone(null)}
            disabled={addM.isPending}
            className="p-1 rounded text-slate-500 hover:text-slate-300 disabled:opacity-50"
            title="Cancel"
          >
            <X size={13} />
          </button>
        </td>
      </tr>
      {err && (
        <tr className="bg-slate-800/30">
          <td colSpan={7} className="px-2 pb-2 text-[11px] text-red-300 flex items-center gap-1">
            <AlertTriangle size={11} /> {err}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Danger zone (delete container) ──────────────────────────────────

function DangerZone({
  id, onDeleted, onBanner,
}: {
  id: string;
  onDeleted: () => void;
  onBanner: (msg: string | null) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const delM = useMutation({
    mutationFn: () => api.deleteContainer(id),
    onSuccess: ({ shpschdErrors }) => {
      if (shpschdErrors.length > 0) {
        onBanner(`Container deleted, but ${shpschdErrors.length} shpschd line(s) failed to roll back in Advantage. They may need manual cleanup.`);
      }
      onDeleted();
    },
    onError: (e: Error) => onBanner(`Delete failed: ${e.message}`),
  });

  return (
    <div className="pt-4 border-t border-slate-800">
      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="text-xs px-3 py-1.5 rounded border border-red-900/50 text-red-300 hover:bg-red-950/30 flex items-center gap-1.5"
        >
          <Trash2 size={13} /> Delete container
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-300">Delete this container and roll back its shpschd lines?</span>
          <button
            onClick={() => delM.mutate()}
            disabled={delM.isPending}
            className="text-xs px-3 py-1.5 rounded bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-1.5"
          >
            {delM.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {delM.isPending ? 'Deleting…' : 'Confirm delete'}
          </button>
          <button
            onClick={() => setConfirming(false)}
            disabled={delM.isPending}
            className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Create modal ────────────────────────────────────────────────────

// A draft line carries the pipeline context it came from (open qty, so the UI
// can flag over-picking). One vendor per shipment, so vendor lives on the header.
interface DraftLine extends ContainerCreateLine {
  openQty?: number;
}

function CreateContainerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [eta, setEta] = useState('');
  const [notes, setNotes] = useState('');
  const [cartons, setCartons] = useState('');
  const [totalCbm, setTotalCbm] = useState('');
  const [totalCases, setTotalCases] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  // One vendor per shipment — captured from the first PO picked.
  const [vendorCode, setVendorCode] = useState<number | null>(null);
  const [vendorName, setVendorName] = useState('');

  // Item loader / PO picker.
  const [itemInput, setItemInput] = useState('');
  const [loadedItem, setLoadedItem] = useState('');

  // Destination: warehouse (default) or drop-ship to a customer.
  const [dropship, setDropship] = useState(false);
  const [dsCustomer, setDsCustomer] = useState<{ code: number | null; name: string } | null>(null);
  const [dsOrderNo, setDsOrderNo] = useState<string | null>(null);

  const backdropMouseDown = useRef(false);

  const pipelineQ = useQuery({
    queryKey: ['purchasing', 'pipeline', loadedItem],
    queryFn: () => api.getPurchasingPipeline(loadedItem),
    enabled: loadedItem.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const pipeline = pipelineQ.data;

  const createM = useMutation({
    mutationFn: (body: ContainerCreateRequest) => api.createContainer(body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['containers'] });
      if (result.shpschdFailures > 0) {
        alert(
          `Shipment created, but ${result.shpschdFailures} of ${result.detail.lines.length} ` +
          `lines failed to write to Advantage shpschd. Open the shipment to see which lines need retry.`
        );
      }
      onCreated();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  function loadItem() {
    const it = itemInput.trim();
    if (!it) return;
    setFormError(null);
    setLoadedItem(it);
  }

  // Add a line from a picked open PO. Enforces one vendor per shipment.
  function addPoLine(po: NonNullable<typeof pipeline>['openVendorPos'][number], itemNo: string) {
    setFormError(null);
    if (vendorCode != null && po.vendorCode != null && po.vendorCode !== vendorCode) {
      setFormError(
        `That PO is vendor ${po.vendorName || po.vendorCode}, but this shipment is for ${vendorName || vendorCode}. ` +
        `A shipment is one vendor — finish this one or start another.`
      );
      return;
    }
    if (vendorCode == null && po.vendorCode != null) {
      setVendorCode(po.vendorCode);
      setVendorName(po.vendorName || String(po.vendorCode));
    }
    // Merge if this PO+item is already a line.
    setLines((curr) => {
      const idx = curr.findIndex((l) => l.poNumber === po.po && l.itemNo === itemNo);
      if (idx >= 0) return curr;   // already added; don't duplicate
      return [...curr, { poNumber: po.po, itemNo, quantity: po.openQty, cbm: null, cases: null, notes: null, openQty: po.openQty }];
    });
  }

  function updateLine(i: number, patch: Partial<DraftLine>) {
    setLines((curr) => curr.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function removeLine(i: number) {
    setLines((curr) => {
      const next = curr.filter((_, idx) => idx !== i);
      if (next.length === 0) { setVendorCode(null); setVendorName(''); }  // vendor unlocks when empty
      return next;
    });
  }

  function pickDropship(cand: NonNullable<typeof pipeline>['dropShipCandidates'][number]) {
    setDsCustomer({ code: cand.customerCode, name: cand.customerName });
    setDsOrderNo(cand.orderNo);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) { setFormError('Shipment name is required'); return; }
    const cleaned = lines
      .map((l) => ({ ...l, poNumber: l.poNumber.trim(), itemNo: l.itemNo.trim() }))
      .filter((l) => l.poNumber && l.itemNo && l.quantity > 0);
    if (cleaned.length === 0) {
      setFormError('Add at least one line by picking an open PO for an item.');
      return;
    }
    if (dropship && !dsCustomer) {
      setFormError('Drop-ship is on — pick a customer order, or switch to Warehouse.');
      return;
    }
    createM.mutate({
      name: name.trim(),
      vendor: vendorName.trim() || null,
      vendorCode: vendorCode,
      shipDate: shipDate || null,
      eta: eta || null,
      cartons: cartons ? Number(cartons) : null,
      totalCbm: totalCbm ? Number(totalCbm) : null,
      totalCases: totalCases ? Number(totalCases) : null,
      notes: notes.trim() || null,
      dropshipCustomerCode: dropship ? dsCustomer?.code ?? null : null,
      dropshipCustomerName: dropship ? dsCustomer?.name ?? null : null,
      dropshipOrderNo: dropship ? dsOrderNo : null,
      lines: cleaned.map(({ openQty: _openQty, ...l }) => l),
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropMouseDown.current) onClose();
        backdropMouseDown.current = false;
      }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
          <div>
            <h3 className="text-lg font-semibold text-white">New shipment</h3>
            <p className="text-xs text-slate-500 mt-0.5">Load an item, pick its open PO(s), set the destination. One vendor per shipment.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-5">
          {/* Header */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name *" value={name} onChange={setName} placeholder="Container 042 / Tel Aviv Pesach" />
            <div>
              <label className="block text-xs text-slate-400 mb-1">Vendor</label>
              <div className="w-full bg-slate-800/40 border border-slate-700 text-sm rounded-md px-2 py-1.5 text-slate-200">
                {vendorCode != null
                  ? <span>{vendorName} <span className="text-slate-500">· {vendorCode}</span></span>
                  : <span className="text-slate-500">Set from the first PO you pick</span>}
              </div>
            </div>
            <Field label="Ship date (origin)" type="date" value={shipDate} onChange={setShipDate} />
            <Field label="ETA (destination)" type="date" value={eta} onChange={setEta} />
            <Field label="Cartons" type="number" value={cartons} onChange={setCartons} placeholder="Optional" />
            <Field label="Total CBM" type="number" value={totalCbm} onChange={setTotalCbm} placeholder="Optional" />
            <Field label="Total cases" type="number" value={totalCases} onChange={setTotalCases} placeholder="Optional" />
          </div>

          {/* Item loader + PO picker */}
          <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-3">
            <label className="text-sm font-medium text-slate-300">Add items — pick open POs</label>
            <div className="flex items-center gap-2">
              <input
                value={itemInput}
                onChange={(e) => setItemInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadItem(); } }}
                placeholder="Item # (e.g. 1120)"
                className="flex-1 bg-slate-800/60 border border-slate-700 text-slate-100 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
              />
              <button
                type="button"
                onClick={loadItem}
                className="px-3 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-sm hover:bg-slate-700 flex items-center gap-1.5"
              >
                {pipelineQ.isFetching ? <RefreshCw size={13} className="animate-spin" /> : <RefreshCw size={13} />} Load
              </button>
            </div>

            {pipelineQ.error && (
              <div className="text-xs text-red-300">Couldn't load pipeline: {(pipelineQ.error as Error).message}</div>
            )}

            {pipeline && (
              <div className="space-y-2">
                <div className="text-xs text-slate-400">
                  <span className="text-slate-200 font-medium">{pipeline.item?.itemNo ?? loadedItem}</span>
                  {pipeline.item?.description ? ` · ${pipeline.item.description}` : ''}
                  {pipeline.item ? ` · avail ${fmtNum(pipeline.item.totalAvailable)}` : ''}
                </div>
                {pipeline.openVendorPos.length === 0 ? (
                  <div className="text-xs text-slate-500">No open vendor POs for this item.</div>
                ) : (
                  <div className="border border-slate-800 rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/60 text-slate-500">
                        <tr>
                          <th className="px-2 py-1.5 text-left">PO</th>
                          <th className="px-2 py-1.5 text-left">Vendor</th>
                          <th className="px-2 py-1.5 text-right">Open</th>
                          <th className="px-2 py-1.5 text-left">ETA</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {pipeline.openVendorPos.map((po) => {
                          const added = lines.some((l) => l.poNumber === po.po && l.itemNo === (pipeline.item?.itemNo ?? loadedItem));
                          const wrongVendor = vendorCode != null && po.vendorCode != null && po.vendorCode !== vendorCode;
                          return (
                            <tr key={po.po} className={wrongVendor ? 'opacity-40' : ''}>
                              <td className="px-2 py-1.5 text-slate-200 tabular-nums">{po.po}</td>
                              <td className="px-2 py-1.5 text-slate-400">{po.vendorName || po.vendorCode || '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">{fmtNum(po.openQty)}</td>
                              <td className={`px-2 py-1.5 tabular-nums ${po.pastEta ? 'text-red-300' : 'text-slate-400'}`}>{fmtDate(po.eta)}</td>
                              <td className="px-2 py-1.5 text-right">
                                <button
                                  type="button"
                                  disabled={added || wrongVendor}
                                  onClick={() => addPoLine(po, pipeline.item?.itemNo ?? loadedItem)}
                                  className="px-1.5 py-0.5 rounded text-[11px] border border-slate-700 text-slate-200 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={wrongVendor ? 'Different vendor — one vendor per shipment' : added ? 'Already added' : 'Add this PO to the shipment'}
                                >
                                  {added ? 'Added' : 'Add'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Picked lines */}
          <div>
            <label className="text-sm font-medium text-slate-300">Shipment lines {lines.length > 0 && <span className="text-slate-500 font-normal">({lines.length})</span>}</label>
            <div className="mt-2 border border-slate-800 rounded overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-slate-400 text-xs">
                  <tr>
                    <th className="px-2 py-1.5 text-left">PO #</th>
                    <th className="px-2 py-1.5 text-left">Item #</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-right">CBM</th>
                    <th className="px-2 py-1.5 text-right">Cases</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-3 text-center text-xs text-slate-600">Load an item above and click “Add” on an open PO.</td></tr>
                  )}
                  {lines.map((l, i) => {
                    const over = l.openQty != null && l.quantity > l.openQty;
                    return (
                      <tr key={`${l.poNumber}-${l.itemNo}`} className="border-t border-slate-800">
                        <td className="px-2 py-1 text-slate-300 tabular-nums">{l.poNumber}</td>
                        <td className="px-2 py-1 text-slate-300">{l.itemNo}</td>
                        <td className="px-1 py-1">
                          <CellInput type="number" value={String(l.quantity || '')} onChange={(v) => updateLine(i, { quantity: Number(v) || 0 })} align="right" />
                          {over && <div className="text-[10px] text-amber-400 text-right pr-1">&gt; open {fmtNum(l.openQty ?? 0)}</div>}
                        </td>
                        <td className="px-1 py-1"><CellInput type="number" value={l.cbm == null ? '' : String(l.cbm)} onChange={(v) => updateLine(i, { cbm: v === '' ? null : Number(v) })} align="right" /></td>
                        <td className="px-1 py-1"><CellInput type="number" value={l.cases == null ? '' : String(l.cases)} onChange={(v) => updateLine(i, { cases: v === '' ? null : Number(v) })} align="right" /></td>
                        <td className="px-1 py-1 w-8">
                          <button type="button" onClick={() => removeLine(i)} className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-950/30" title="Remove line">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Destination */}
          <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3 space-y-2">
            <label className="text-sm font-medium text-slate-300">Destination</label>
            <div className="flex gap-2 text-sm">
              <button type="button" onClick={() => { setDropship(false); }}
                className={`px-3 py-1 rounded-md border ${!dropship ? 'bg-blue-500/15 border-blue-500/40 text-blue-100' : 'border-slate-700 text-slate-400'}`}>
                Warehouse
              </button>
              <button type="button" onClick={() => setDropship(true)}
                className={`px-3 py-1 rounded-md border ${dropship ? 'bg-blue-500/15 border-blue-500/40 text-blue-100' : 'border-slate-700 text-slate-400'}`}>
                Drop-ship to customer
              </button>
            </div>
            {dropship && (
              <div className="space-y-2">
                {dsCustomer ? (
                  <div className="text-xs text-slate-300 flex items-center gap-2">
                    Direct to <span className="text-emerald-300 font-medium">{dsCustomer.name || dsCustomer.code}</span>
                    {dsOrderNo && <span className="text-slate-500">· order {dsOrderNo}</span>}
                    <button type="button" onClick={() => { setDsCustomer(null); setDsOrderNo(null); }} className="text-slate-500 hover:text-red-400">clear</button>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">Pick a customer order below.</div>
                )}
                {pipeline && pipeline.dropShipCandidates.length > 0 ? (
                  <div className="border border-slate-800 rounded overflow-x-auto max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900/60 text-slate-500"><tr>
                        <th className="px-2 py-1.5 text-left">Order</th>
                        <th className="px-2 py-1.5 text-left">Customer</th>
                        <th className="px-2 py-1.5 text-right">Open</th>
                        <th />
                      </tr></thead>
                      <tbody className="divide-y divide-slate-800/60">
                        {pipeline.dropShipCandidates.map((c) => (
                          <tr key={c.orderNo}>
                            <td className="px-2 py-1.5 text-slate-300 tabular-nums">{c.orderNo}</td>
                            <td className="px-2 py-1.5 text-slate-400">{c.customerName || c.customerCode || '—'}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">{fmtNum(c.openQty)}</td>
                            <td className="px-2 py-1.5 text-right">
                              <button type="button" onClick={() => pickDropship(c)} className="px-1.5 py-0.5 rounded text-[11px] border border-slate-700 text-slate-200 hover:bg-slate-700">Pick</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-xs text-slate-600">{pipeline ? 'No open customer orders for the loaded item.' : 'Load an item to see drop-ship candidates.'}</div>
                )}
              </div>
            )}
          </div>

          <Field label="Notes" value={notes} onChange={setNotes} placeholder="Free-form" multiline />

          {formError && (
            <div className="text-sm text-red-300 bg-red-950/30 border border-red-900/40 rounded px-3 py-2 flex items-center gap-2">
              <AlertTriangle size={14} /> {formError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createM.isPending}
              className="px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/40 text-blue-100 hover:bg-blue-500/30 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {createM.isPending && <RefreshCw size={12} className="animate-spin" />}
              {createM.isPending ? 'Creating…' : 'Create shipment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', placeholder, multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  multiline?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="w-full bg-slate-800/60 border border-slate-700 text-slate-100 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
        />
      ) : (
        <>
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-slate-800/60 border border-slate-700 text-slate-100 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50 ${type === 'date' ? '[color-scheme:dark]' : ''}`}
          />
          {type === 'date' && hebrewDateCaption(value) && (
            <span className="block mt-0.5 text-[10px] text-slate-500 leading-3">{hebrewDateCaption(value)}</span>
          )}
        </>
      )}
    </div>
  );
}

function CellInput({
  value, onChange, type = 'text', align = 'left',
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  align?: 'left' | 'right';
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500/50 text-${align}`}
    />
  );
}

export default PurchasingPipelineTab;
